import { useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, eur, fmtDateTime, errMsg } from "@/lib/api";
import { Loader, StatusBadge, Chip, DocButton } from "@/components/common";
import { useList, post, Table, useDialog, PageTitle } from "@/pages/admin/kit";
import { usePrices } from "@/pages/Pricing";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function FinanceRow({ label, value, strong, tone, hint }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-line last:border-0">
      <span className={strong ? "font-bold" : "w-muted"}>{label}{hint && <span className="block text-xs w-muted font-normal">{hint}</span>}</span>
      <span className={`tabular-nums ${strong ? "font-bold" : ""} ${tone === "mint" ? "text-mint-ink" : tone === "warn" ? "text-[var(--w-warn)]" : ""}`}>{value}</span>
    </div>
  );
}

function BreakdownBtn({ booking }) {
  const [open, setOpen] = useState(false);
  const [fin, setFin] = useState(null);
  const load = async () => {
    setOpen(true); setFin(null);
    try { const { data } = await api.get(`/admin/bookings/${booking.id}/finance`); setFin(data); }
    catch (e) { setFin({ error: errMsg(e) }); }
  };
  const pct = fin ? Math.round(fin.commission_rate * 100) : 25;
  return (
    <>
      <button className="w-btn w-btn-secondary !min-h-[40px] !px-4" data-testid={`finance-${booking.id}`} onClick={load}>Abrechnung</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="finance-breakdown-dialog">
          <DialogHeader><DialogTitle>Abrechnung · {booking.code}</DialogTitle></DialogHeader>
          {!fin ? <Loader /> : fin.error ? <p className="text-[var(--w-error)]">{fin.error}</p> : (
            <div className="space-y-1 text-sm">
              {!fin.paid && <p className="w-muted mb-2">Für diese Buchung liegt noch keine Zahlung vor. Auftragssumme: {eur(fin.order_total)}.</p>}
              {fin.paid && <>
                <FinanceRow label="Vom Kunden bezahlt" value={eur(fin.total_paid)} strong />
                <FinanceRow label={`Waschheld-Anteil (${100 - pct} %)`} value={eur(fin.provider_share)} tone="mint" strong />
                <FinanceRow label={`Washly-Bruttoprovision (${pct} %)`} value={eur(fin.washly_gross)} />
                <FinanceRow label="Zahlungsgebühren (Mollie)" value={`−${eur(fin.psp_fee)}`} tone="warn" hint="trägt Washly aus seinem Anteil" />
                <FinanceRow label="Washly-Nettoerlös" value={eur(fin.washly_net)} strong />
                {fin.refunded > 0 && <FinanceRow label="Bereits erstattet" value={`−${eur(fin.refunded)}`} tone="warn" />}
              </>}
              <div className="pt-3 space-y-2">
                <div className="flex items-center gap-2"><span className="w-muted">Zahlungsstatus:</span><Chip tone={fin.payment_status === "paid" ? "mint" : "neutral"}>{fin.payment_status}</Chip><span className="w-muted text-xs">{fin.psp}</span></div>
                <div className="flex items-center gap-2 flex-wrap"><span className="w-muted">Auszahlung:</span>
                  {fin.payouts.length === 0 ? <Chip tone="neutral">noch keine</Chip> : fin.payouts.map((p, i) => (
                    <Chip key={i} tone={p.status === "transferred" || p.status === "released" ? "mint" : p.status === "held" ? "warn" : "info"}>
                      {({ transferred: "transferiert", released: "ausgezahlt", pending: "ausstehend", held: "gesperrt" }[p.status] || p.status)} · {eur(p.amount)}{p.reversed_amount ? ` (−${eur(p.reversed_amount)} rückabgewickelt)` : ""}
                    </Chip>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap"><span className="w-muted">Auszahlungskonto (IBAN):</span>
                  {fin.provider_payout_account?.has_iban
                    ? <span className="font-mono text-sm" data-testid="finance-provider-iban">{fin.provider_payout_account.iban}{fin.provider_payout_account.account_holder ? ` · ${fin.provider_payout_account.account_holder}` : ""}</span>
                    : <Chip tone="warn">keine IBAN hinterlegt</Chip>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TrendsChart({ trends }) {
  if (!trends?.length) return null;
  return (
    <div className="w-card p-5 mb-6" data-testid="dashboard-trends">
      <h2 className="font-bold mb-1">Trends der letzten 8 Wochen</h2>
      <p className="w-muted text-sm mb-4">Buchungen (Balken) und Umsatz in € (Linie) je Kalenderwoche</p>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={trends} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#eef1f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} width={34} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12, fill: "#047857" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${v}€`} />
          <Tooltip formatter={(v, n) => (n === "Umsatz" ? [eur(v), n] : [v, n])} contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 13 }} />
          <Bar yAxisId="l" dataKey="bookings" name="Buchungen" fill="#19E6B3" radius={[6, 6, 0, 0]} maxBarSize={38} />
          <Line yAxisId="r" type="monotone" dataKey="revenue" name="Umsatz" stroke="#047857" strokeWidth={2.5} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Dashboard() {
  const { data: s } = useList("/admin/stats");
  const { data: site } = useList("/site-status");
  if (!s) return <Loader />;
  const live = site?.payments_live;
  const sub = live === undefined ? "" : live
    ? "Live-Modus · echte Mollie-Zahlungen aktiv"
    : "Sandbox/Test-Modus · keine echten Geldflüsse (Test-Key)";
  const cards = [["Buchungen", s.bookings_total], ["Anbieter offen", s.providers_pending], ["Anbieter aktiv", s.providers_approved], ["Kunden", s.customers],
    ["Reklamationen offen", s.complaints_open], ["Versicherung offen", s.insurance_open], ["Tickets offen", s.tickets_open], ["Bewertungen geflaggt", s.reviews_flagged]];
  return (
    <div>
      <PageTitle sub={sub}>Dashboard</PageTitle>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map(([l, v]) => <div key={l} className="w-card p-4"><p className="w-label">{l}</p><p className="text-3xl font-extrabold" data-testid={`stat-${l}`}>{v}</p></div>)}
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {[["GMV", s.gmv], ["Provision", s.commission], ["Erstattet", s.refunded]].map(([l, v]) => <div key={l} className="w-card p-5 bg-mint-soft"><p className="w-label">{l}</p><p className="text-3xl font-extrabold">{eur(v)}</p></div>)}
      </div>
      <TrendsChart trends={s.trends} />
      <div className="w-card p-5"><h2 className="font-bold mb-3">Buchungen nach Status</h2><div className="flex flex-wrap gap-2">{Object.entries(s.bookings_by_status).map(([k, v]) => <span key={k} className="flex items-center gap-1"><StatusBadge status={k} /> <b>{v}</b></span>)}</div></div>
    </div>
  );
}

const TIERS = [["standard", "Standard (25 %)"], ["pro", "Pro (24 %)"], ["elite", "Elite (22,5 %)"]];

export function Providers() {
  const { data, reload } = useList("/admin/providers");
  const px = usePrices();
  const [dlg, open] = useDialog();
  const [detailId, setDetailId] = useState(null);
  const act = (p, action) => open({
    title: `${p.display_name}: ${action}`,
    fields: [{ name: "tier", label: "Tier", options: TIERS, default: p.tier }, ...(action !== "set_tier" ? [{ name: "note", label: "Notiz" }] : [])],
    onSubmit: (v) => post(`/admin/providers/${p.id}`, { action, ...v }, "Aktualisiert", reload),
  });
  if (!data) return <Loader />;
  const sorted = [...data].sort((a, b) => (a.status === "pending_review" ? -1 : 1) - (b.status === "pending_review" ? -1 : 1));
  return (
    <div>
      <PageTitle sub="Freigabe, Tier-Zuweisung, Sperrung">Anbieter</PageTitle>
      {dlg}
      {detailId && <ProviderDetail pid={detailId} onClose={() => setDetailId(null)} />}
      <Table testId="admin-providers-table" rows={sorted} cols={[
        ["Anbieter", (p) => <div><p className="font-semibold">{p.display_name}</p><p className="w-muted text-sm">{p.email} · {p.city || "–"}</p></div>],
        ["Status", (p) => <Chip tone={p.status === "approved" ? "mint" : p.status === "pending_review" ? "info" : p.status === "draft" ? "neutral" : "error"}>{p.status}</Chip>],
        ["Ausweis / KYC", (p) => (
          <div className="flex flex-col gap-1.5 items-start">
            {p.kyc_status === "verified" ? <Chip tone="mint"><BadgeCheck size={14} aria-hidden="true" /> verifiziert</Chip> : <Chip tone="warn">{p.kyc_status || "offen"}</Chip>}
            <DocButton fileId={p.kyc_file_id} label="Ausweis ansehen" testId={`kyc-view-${p.id}`} />
            {p.kyc_file_id && p.kyc_status !== "verified" && <button className="w-btn w-btn-primary !min-h-[34px] !px-3" onClick={() => post(`/admin/providers/${p.id}/kyc`, { decision: "verify" }, "KYC freigegeben", reload)} data-testid={`kyc-verify-${p.id}`}>Freigeben</button>}
            {p.kyc_file_id && p.kyc_status === "verified" && <button className="w-btn w-btn-secondary !min-h-[34px] !px-3" onClick={() => post(`/admin/providers/${p.id}/kyc`, { decision: "reject", note: "Nachprüfung" }, "KYC zurückgezogen", reload)} data-testid={`kyc-reject-${p.id}`}>Ablehnen</button>}
          </div>
        )],
        ["Gewerbe", (p) => `${p.business_type || "–"}${p.company_name ? ` · ${p.company_name}` : ""}`],
        ["Tier", (p) => p.tier],
        ["Preis/kg", () => eur(px?.base ?? 2.99)],
        ["Aktionen", (p) => (
          <div className="flex flex-wrap gap-2">
            <button className="w-btn w-btn-secondary !min-h-[40px] !px-4" onClick={() => setDetailId(p.id)} data-testid={`detail-${p.id}`}>Antrag prüfen</button>
            {p.status !== "approved" && <button className="w-btn w-btn-primary !min-h-[40px] !px-4" onClick={() => act(p, "approve")} data-testid={`approve-${p.id}`}>Freigeben</button>}
            {p.status === "pending_review" && <button className="w-btn w-btn-danger !min-h-[40px] !px-4" onClick={() => act(p, "reject")} data-testid={`reject-${p.id}`}>Ablehnen</button>}
            {p.status === "approved" && <button className="w-btn w-btn-secondary !min-h-[40px] !px-4" onClick={() => act(p, "set_tier")} data-testid={`tier-${p.id}`}>Tier</button>}
            {p.status === "approved" && <button className="w-btn w-btn-danger !min-h-[40px] !px-4" onClick={() => act(p, "suspend")} data-testid={`suspend-${p.id}`}>Sperren</button>}
          </div>
        )],
      ]} />
    </div>
  );
}

const SVC_LABELS = { dryer: "Trockner", folding: "Falten", ironing: "Bügeln", pickup: "Abholung", return: "Rücklieferung", softener: "Weichspüler", perfume: "Wäscheparfum", sensitive: "Sensitiv-Waschmittel", self_pickup_allowed: "Selbstabholung erlaubt" };
const ATTR_LABELS_A = { smoke_free: "Rauchfrei", pet_free: "Haustierfrei", eco_detergent: "Öko-/Bio", allergy_friendly: "Allergikerfreundlich", steam_ironing: "Dampfbügeln", express: "Express", flexible_times: "Flexible Zeiten" };
const DAYS_A = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function Row({ label, children }) {
  return <div className="flex gap-2 text-sm"><span className="w-muted min-w-[150px]">{label}</span><span className="font-medium">{children ?? "–"}</span></div>;
}

function ProviderDetail({ pid, onClose }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/providers/${pid}/detail`).then((r) => setD(r.data)).catch(() => setD({}));
  if (!d) { load(); return <div className="w-card p-5 mb-4" data-testid="provider-detail">Lädt…</div>; }
  const p = d.profile || {}, priv = d.private || {}, u = d.user || {};
  const svc = p.services || {}, at = p.attributes || {}, cal = p.calendar || {};
  const days = Object.keys(cal.weekly || {}).filter((k) => (cal.weekly[k] || []).length).map((k) => DAYS_A[+k]);
  return (
    <div className="w-card p-5 mb-4 space-y-4" data-testid="provider-detail">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xl font-extrabold">Antrag prüfen: {p.display_name}</h3>
        <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={onClose} data-testid="provider-detail-close">Schließen</button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <h4 className="font-bold">Konto & Profil</h4>
          <Row label="Name (Konto)">{u.name}</Row>
          <Row label="E-Mail">{u.email}</Row>
          <Row label="Registriert">{u.created_at ? fmtDateTime(u.created_at) : "–"}</Row>
          <Row label="Anzeigename">{p.display_name}</Row>
          <Row label="Über mich">{p.bio}</Row>
          <Row label="Status">{p.status} · KYC: {p.kyc_status || "offen"}</Row>
        </div>
        <div className="space-y-2">
          <h4 className="font-bold">Adresse & Kontakt (privat)</h4>
          <Row label="Straße">{priv.street}</Row>
          <Row label="PLZ / Ort">{[priv.plz, priv.city].filter(Boolean).join(" ")}</Row>
          <Row label="Telefon">{priv.phone}</Row>
          <h4 className="font-bold pt-2">Gewerbestatus</h4>
          <Row label="Typ">{p.business_type === "commercial" ? "Gewerblich" : "Privat"}</Row>
          {p.business_type === "commercial" && <><Row label="Firma">{priv.company_name}</Row><Row label="USt-IdNr.">{priv.tax_id}</Row></>}
        </div>
      </div>
      <div>
        <h4 className="font-bold mb-1">Leistungen</h4>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(SVC_LABELS).filter((k) => svc[k]).map((k) => <Chip key={k} tone="mint">{SVC_LABELS[k]}</Chip>)}
          {!Object.keys(SVC_LABELS).some((k) => svc[k]) && <span className="w-muted text-sm">Keine angegeben</span>}
        </div>
      </div>
      <div>
        <h4 className="font-bold mb-1">Besonderheiten</h4>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(ATTR_LABELS_A).filter((k) => at[k]).map((k) => <Chip key={k} tone="info">{ATTR_LABELS_A[k]}</Chip>)}
          {!Object.keys(ATTR_LABELS_A).some((k) => at[k]) && <span className="w-muted text-sm">Keine</span>}
        </div>
      </div>
      <div>
        <h4 className="font-bold mb-1">Kalender</h4>
        <p className="text-sm">Tage: {days.length ? days.join(", ") : "–"} · Kapazität/Fenster: {cal.capacity_per_slot || 1}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
        <h4 className="font-bold">Ausweisdokument (KYC):</h4>
        <DocButton fileId={priv.kyc_file_id} label="Ausweis ansehen" testId={`detail-kyc-${pid}`} />
        <span className="text-sm w-muted">Status: {p.kyc_status || "offen"}</span>
        {d.insurance_policy && <span className="text-sm w-muted">· Versicherung: {p.insurance_status || "none"}</span>}
      </div>
    </div>
  );
}

export function Bookings() {
  const { data, reload } = useList("/admin/bookings");
  const [dlg, open] = useDialog();
  const [q, setQ] = useState("");
  if (!data) return <Loader />;
  const ql = q.trim().toLowerCase();
  const rows = ql ? data.filter((b) => (b.code || "").toLowerCase().includes(ql) || (b.customer_name || "").toLowerCase().includes(ql) || (b.provider_name || "").toLowerCase().includes(ql)) : data;
  return (
    <div>
      <PageTitle sub="Alle Aufträge · Suche per Auftragsnummer · manuelle (Teil-)Rückerstattung">Buchungen</PageTitle>
      {dlg}
      <input className="w-input max-w-sm mb-4" placeholder="Suche: Auftragsnummer, Kunde oder Anbieter" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-bookings-search" />
      <Table testId="admin-bookings-table" rows={rows} cols={[
        ["Code", (b) => <span><Link className="font-semibold underline" to={`/bookings/${b.id}`}>{b.code}</Link>{b.heavy_soil && <span className="block text-xs font-semibold text-[var(--w-warn)]">⚠ stark verunreinigt</span>}{b.delicate && <span className="block text-xs font-semibold text-[var(--w-mint-ink)]">empfindliche Textilien</span>}</span>],
        ["Kunde → Anbieter", (b) => `${b.customer_name} → ${b.provider_name}`],
        ["Termin", (b) => `${b.date} · ${b.slot}`],
        ["Status", (b) => <StatusBadge status={b.status} />],
        ["Betrag", (b) => <span className="tabular-nums">{eur(b.price.total)}{b.refunded_amount ? <span className="block text-sm text-[var(--w-warn)]">−{eur(b.refunded_amount)}</span> : null}</span>],
        ["Abrechnung", (b) => <BreakdownBtn booking={b} />],
        ["", (b) => b.status !== "requested" && b.status !== "accepted" && !["declined", "cancelled", "expired"].includes(b.status) && (
          <button className="w-btn w-btn-secondary !min-h-[40px] !px-4" data-testid={`refund-${b.id}`} onClick={() => open({
            title: `Rückerstattung ${b.code}`, fields: [{ name: "amount", label: "Betrag (€)", type: "number", default: b.price.total - (b.refunded_amount || 0) }, { name: "reason", label: "Grund", default: "Kulanz" }],
            onSubmit: (v) => post(`/admin/bookings/${b.id}/refund`, { amount: +v.amount, reason: v.reason }, "Erstattet", reload),
          })}>Erstatten</button>
        )],
      ]} />
    </div>
  );
}

export function Complaints() {
  const { data, reload } = useList("/admin/complaints");
  const [dlg, open] = useDialog();
  if (!data) return <Loader />;
  return (
    <div>
      <PageTitle sub="Streitabwicklung · Voll-/Teilerstattung oder Ablehnung">Reklamationen</PageTitle>
      {dlg}
      <Table testId="admin-complaints-table" rows={data} cols={[
        ["Buchung", (c) => <Link className="underline font-semibold" to={`/bookings/${c.booking_id}`}>{c.booking_code}</Link>],
        ["Art", (c) => <Chip tone={["damage", "loss"].includes(c.type) ? "error" : "warn"}>{c.type}</Chip>],
        ["Beschreibung", (c) => <p className="max-w-sm">{c.description}</p>],
        ["Gefordert", (c) => eur(c.amount_claimed)],
        ["Status", (c) => <Chip tone={c.status === "open" ? "info" : "mint"}>{c.status}{c.decision ? ` · ${c.decision}` : ""}</Chip>],
        ["", (c) => c.status === "open" && <button className="w-btn w-btn-primary !min-h-[40px] !px-4" data-testid={`resolve-${c.id}`} onClick={() => open({
          title: `Reklamation ${c.booking_code} entscheiden`,
          fields: [{ name: "decision", label: "Entscheidung", options: [["refund_partial", "Teilerstattung"], ["refund_full", "Vollerstattung"], ["reject", "Ablehnen"]], default: "refund_partial" },
            { name: "amount", label: "Betrag bei Teilerstattung (€)", type: "number", default: 0 }, { name: "note", label: "Notiz" }],
          onSubmit: (v) => post(`/admin/complaints/${c.id}/resolve`, { ...v, amount: +v.amount }, "Entschieden", reload),
        })}>Entscheiden</button>],
      ]} />
    </div>
  );
}

export function Insurance() {
  const { data, reload } = useList("/admin/insurance");
  const [dlg, open] = useDialog();
  if (!data) return <Loader />;
  return (
    <div>
      <PageTitle sub="Schadensmeldung → manuelle Einzelfallprüfung. Keine automatische oder garantierte Regulierung.">Reklamationen (Schäden)</PageTitle>
      {dlg}
      <Table testId="admin-insurance-table" rows={data} cols={[
        ["Buchung", (c) => <Link className="underline font-semibold" to={`/bookings/${c.booking_id}`}>{c.booking_code}</Link>],
        ["Gemeldet", (c) => eur(c.amount_claimed)],
        ["Versicherungsstatus (Buchung)", (c) => <Chip tone={c.provider_insurance_status === "premium" ? "mint" : c.provider_insurance_status === "verified" ? "info" : "neutral"}>{c.provider_insurance_status || "none"}</Chip>],
        ["Status", (c) => <Chip tone={c.status === "paid" ? "mint" : c.status === "rejected" ? "error" : "info"}>{c.status}</Chip>],
        ["Nachweise", (c) => (
          <div className="flex flex-col gap-1.5 items-start" data-testid={`evidence-${c.id}`}>
            {c.evidence?.before && <DocButton fileId={c.evidence.before} label="Foto vorher" testId={`ev-before-${c.id}`} />}
            {c.evidence?.after && <DocButton fileId={c.evidence.after} label="Foto nachher" testId={`ev-after-${c.id}`} />}
            {c.evidence?.id_document && <DocButton fileId={c.evidence.id_document} label="Kunden-Ausweis" testId={`ev-id-${c.id}`} />}
            {!c.evidence?.before && !c.evidence?.after && !c.evidence?.id_document && <span className="w-muted text-sm">keine</span>}
          </div>
        )],
        ["Ausgezahlt", (c) => (c.amount_paid ? eur(c.amount_paid) : "–")],
        ["", (c) => !["paid", "rejected"].includes(c.status) && <button className="w-btn w-btn-primary !min-h-[40px] !px-4" data-testid={`claim-${c.id}`} onClick={() => open({
          title: `Versicherungsfall ${c.booking_code}`,
          fields: [{ name: "decision", label: "Aktion", options: [["review", "In Prüfung"], ["approve", "Erstattung veranlassen"], ["reject", "Ablehnen"]], default: c.status === "reported" ? "review" : "approve" },
            { name: "amount", label: "Betrag (€)", type: "number", default: c.amount_claimed }, { name: "note", label: "Notiz" }],
          onSubmit: (v) => post(`/admin/insurance/${c.id}`, { ...v, amount: +v.amount }, "Aktualisiert", reload),
        })}>Bearbeiten</button>],
      ]} />
    </div>
  );
}

export function Tickets() {
  const { data, reload } = useList("/admin/tickets");
  if (!data) return <Loader />;
  const tone = { urgent: "error", high: "warn", medium: "info", low: "neutral" };
  return (
    <div>
      <PageTitle sub="KI-Erstklassifikation (GPT) · Kategorie, Priorität, Team">Support-Tickets</PageTitle>
      <Table testId="admin-tickets-table" rows={data} cols={[
        ["Ticket", (t) => <div><p className="font-semibold">{t.subject}</p><p className="w-muted text-sm">{t.user_email} · {fmtDateTime(t.created_at)}</p><p className="max-w-md mt-1">{t.body}</p></div>],
        ["KI-Triage", (t) => <div className="space-y-1"><Chip tone={tone[t.priority] || "neutral"}>{t.priority}</Chip> <Chip tone="info">{t.category}</Chip><p className="w-muted text-sm max-w-xs">{t.triage?.summary}{t.triage?.ai === false ? " (Fallback)" : ""}</p><p className="text-sm">Team: {t.triage?.suggested_team}</p></div>],
        ["Status", (t) => (
          <select className="w-input !h-10" value={t.status} onChange={(e) => post(`/admin/tickets/${t.id}`, { status: e.target.value }, "Status geändert", reload)} data-testid={`ticket-status-${t.id}`} aria-label="Status">
            {["open", "in_progress", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )],
      ]} />
    </div>
  );
}

export function Reviews() {
  const { data, reload } = useList("/admin/reviews");
  if (!data) return <Loader />;
  const sorted = [...data].sort((a, b) => (b.status === "flagged") - (a.status === "flagged"));
  return (
    <div>
      <PageTitle sub="KI-Moderation (Claude Haiku 4.5) · geflaggte Bewertungen sind bis zur Freigabe unsichtbar">Bewertungen</PageTitle>
      <Table testId="admin-reviews-table" rows={sorted} cols={[
        ["Bewertung", (r) => <div><p className="font-semibold">{"★".repeat(r.rating)} <span className="w-muted font-normal">{r.author_name} ({r.author_role})</span></p><p className="max-w-md">{r.text || "–"}</p></div>],
        ["Moderation", (r) => <div><Chip tone={r.status === "flagged" ? "error" : r.status === "hidden" ? "neutral" : "mint"}>{r.status}</Chip><p className="w-muted text-sm max-w-xs mt-1">{r.moderation?.reason}</p></div>],
        ["", (r) => <div className="flex gap-2">
          {r.status !== "published" && <button className="w-btn w-btn-primary !min-h-[40px] !px-4" onClick={() => post(`/admin/reviews/${r.id}`, { action: "publish" }, "Veröffentlicht", reload)} data-testid={`review-publish-${r.id}`}>Freigeben</button>}
          {r.status !== "hidden" && <button className="w-btn w-btn-danger !min-h-[40px] !px-4" onClick={() => post(`/admin/reviews/${r.id}`, { action: "hide" }, "Ausgeblendet", reload)} data-testid={`review-hide-${r.id}`}>Ausblenden</button>}
        </div>],
      ]} />
    </div>
  );
}
